import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { ScrollReveal } from './ScrollAnimations';
import { ShimmerButton, GlassButton } from './PremiumButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogSection = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/articles?limit=3`);
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="blog" className="py-20 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null; // Don't show section if no articles
  }

  return (
    <section id="blog" className="py-20 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
        {/* Header */}
        <ScrollReveal animation="fadeUp">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white/60 text-sm mb-6">
              <BookOpen size={16} className="text-accent" />
              Blog SEO
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Derniers <span className="text-accent">articles</span>
            </h2>
            <p className="text-white/60">
              Conseils et actualités pour améliorer votre visibilité en ligne
            </p>
          </div>
        </ScrollReveal>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {articles.map((article, index) => (
            <ScrollReveal key={article.id} animation="fadeUp" delay={index * 100}>
              <Link 
                to={`/blog/${article.slug}`}
                className="group block h-full"
              >
                <article className="h-full bg-white/[0.02] border border-white/10 hover:border-accent/30 transition-all duration-300">
                  {/* Image */}
                  <div className="aspect-video bg-gradient-to-br from-accent/10 to-purple-500/10 relative overflow-hidden">
                    {article.image ? (
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white/10">SEO</span>
                      </div>
                    )}
                    {article.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-accent text-black text-xs font-medium">
                        À la une
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <span className="text-accent text-xs font-medium">{article.category}</span>
                    <h3 className="text-lg font-semibold text-white mt-2 mb-3 group-hover:text-accent transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-white/50 text-sm line-clamp-2 mb-4">{article.excerpt}</p>
                    
                    <div className="flex items-center justify-between text-white/40 text-xs">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(article.published_at).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {article.read_time} min
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            </ScrollReveal>
          ))}
        </div>

        {/* CTA */}
        <ScrollReveal animation="fadeUp" delay={400}>
          <div className="text-center">
            <Link to="/blog">
              <GlassButton>
                Voir tous les articles
              </GlassButton>
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default BlogSection;
